import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { SaveOutlined as SaveIcon } from '@mui/icons-material';
import { APP_SHAPE } from '../../constants/shape';
import DialogHeader from '../common/DialogHeader';

interface ZoneFormDialogProps {
  open: boolean;
  saving: boolean;
  submitError?: string | null;
  initialName?: string;
  initialColor?: string;
  onClose: () => void;
  onSubmit: (name: string, color: string) => Promise<boolean>;
}

const PRESET_COLORS = ['#F4841A', '#2CB259', '#0000FE', '#7C3AED', '#DC2626', '#C4640D'];

export default function ZoneFormDialog({
  open,
  saving,
  submitError,
  initialName,
  initialColor,
  onClose,
  onSubmit,
}: ZoneFormDialogProps) {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [color, setColor] = useState(PRESET_COLORS[0]);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    if (!open) return;
    setName(initialName ?? '');
    setColor(initialColor ?? PRESET_COLORS[0]);
    setSubmitted(false);
  }, [open, initialName, initialColor]);

  const handleSubmit = async () => {
    setSubmitted(true);
    if (!name.trim()) return;
    const success = await onSubmit(name.trim(), color);
    if (success) onClose();
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="xs">
      <DialogHeader title={t('zones.form.title')} subtitle={t('zones.form.subtitle')} />
      <DialogContent sx={{ p: { xs: 2, sm: 3 } }}>
        <Stack spacing={3}>
          {submitError && <Alert severity="error">{submitError}</Alert>}
          <TextField
            label={t('zones.form.name')}
            value={name}
            onChange={(event) => setName(event.target.value)}
            required
            autoFocus
            error={submitted && !name.trim()}
            helperText={submitted && !name.trim() ? t('zones.form.nameError') : ' '}
            sx={{ '& .MuiOutlinedInput-root': { borderRadius: `${APP_SHAPE.radiusSm}px` } }}
          />
          <Stack spacing={1}>
            <Typography variant="caption" color="text.secondary">{t('zones.form.color')}</Typography>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              {PRESET_COLORS.map((preset) => (
                <Button
                  key={preset}
                  onClick={() => setColor(preset)}
                  sx={{
                    minWidth: 0,
                    width: 32,
                    height: 32,
                    borderRadius: '50%',
                    bgcolor: preset,
                    border: color === preset ? '3px solid' : '1px solid',
                    borderColor: color === preset ? 'text.primary' : 'divider',
                    '&:hover': { bgcolor: preset },
                  }}
                />
              ))}
              <Box
                component="label"
                sx={{
                  width: 32,
                  height: 32,
                  borderRadius: '50%',
                  border: '1px solid',
                  borderColor: 'divider',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  overflow: 'hidden',
                  bgcolor: !PRESET_COLORS.includes(color) ? color : 'transparent',
                  backgroundImage: PRESET_COLORS.includes(color)
                    ? 'conic-gradient(red, yellow, lime, cyan, blue, magenta, red)'
                    : 'none',
                }}
              >
                <input
                  type="color"
                  value={color}
                  onChange={(event) => setColor(event.target.value)}
                  style={{ opacity: 0, width: 1, height: 1, position: 'absolute' }}
                  aria-label={t('zones.form.customColor')}
                />
              </Box>
              <TextField
                size="small"
                value={color}
                onChange={(event) => setColor(event.target.value)}
                sx={{ width: 110, '& .MuiOutlinedInput-root': { borderRadius: `${APP_SHAPE.radiusSm}px` } }}
                inputProps={{ maxLength: 7, style: { fontFamily: 'monospace' } }}
              />
            </Stack>
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: { xs: 2, sm: 3 }, py: 2, borderTop: 1, borderColor: 'divider' }}>
        <Button onClick={onClose} disabled={saving}>{t('zones.form.cancel')}</Button>
        <Button
          variant="contained"
          startIcon={saving ? <CircularProgress color="inherit" size={16} /> : <SaveIcon />}
          onClick={() => void handleSubmit()}
          disabled={saving}
        >
          {saving ? t('zones.form.saving') : t('zones.form.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
